import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CloseOutlined } from '@ant-design/icons';
import { Overlay, Panel, PanelHeader, PanelTitle, CloseButton, PanelBody } from './SidePanel.style';

/**
 * Reusable side panel that overlays from the right (like a drawer).
 * Change only the content (children) per screen; panel shell is shared.
 *
 * @param {boolean} open - Whether panel is visible
 * @param {() => void} onClose - Called when overlay or close button is clicked
 * @param {boolean} [closeOnOverlayClick=true] - When false, clicking outside won't close the panel
 * @param {string} [title] - Optional header title
 * @param {React.ReactNode} [children] - Panel body content
 * @param {string|number} [width] - Panel width: CSS string (e.g. '480px', '40%', '50vw') or number (treated as px). Default 480px.
 * @param {string|number} [minWidth] - Optional min-width (same format as width)
 * @param {string|number} [maxWidth] - Optional max-width (same format as width)
 * @param {string} [bodyPadding] - Optional body padding override (CSS padding value). Default matches panel design.
 * @param {'auto'|'hidden'|'scroll'} [bodyOverflowY] - Optional vertical overflow for body. Default 'auto'.
 * @param {number} [zIndex] - Optional z-index for overlay/panel (use > 1100 to sit above AppModal).
 */
const normalizeWidth = (v) => (typeof v === 'number' ? `${v}px` : (v ?? '480px'));

const SidePanel = ({
    open,
    onClose,
    closeOnOverlayClick = true,
    title,
    children,
    width = '480px',
    minWidth,
    maxWidth,
    bodyPadding,
    bodyOverflowY,
    zIndex,
}) => {
    const widthCss = normalizeWidth(width);
    const minWidthCss = minWidth != null ? normalizeWidth(minWidth) : undefined;
    const maxWidthCss = maxWidth != null ? normalizeWidth(maxWidth) : undefined;
    const overlayZ = zIndex != null ? Math.max(0, zIndex - 10) : undefined;
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    const content = (
        <>
            <Overlay
                $open={open}
                $zIndex={overlayZ}
                onClick={closeOnOverlayClick ? onClose : undefined}
                aria-hidden="true"
            />
            <Panel
                $open={open}
                $width={widthCss}
                $minWidth={minWidthCss}
                $maxWidth={maxWidthCss}
                $zIndex={zIndex}
                onClick={(e) => e.stopPropagation()}
            >
                <PanelHeader>
                    {title && <PanelTitle>{title}</PanelTitle>}
                    <CloseButton type="button" onClick={onClose} aria-label="Close">
                        <CloseOutlined />
                    </CloseButton>
                </PanelHeader>
                <PanelBody $padding={bodyPadding} $overflowY={bodyOverflowY}>
                    {children}
                </PanelBody>
            </Panel>
        </>
    );

    return typeof document !== 'undefined' ? createPortal(content, document.body) : null;
};

export default SidePanel;
